import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, formatApiError } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch (e) {
      // Expected on first-load for anonymous visitors (401). Don't log
      // — this path is the canonical signal that the user is not signed in.
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await authAPI.login(email, password);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await authAPI.register(email, password, name);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await authAPI.logout();
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch (e) {
      // Refresh races a logout or a session expiry — log but don't surface
      // since the next checkAuth/route guard will resolve user state.
      if (e?.response?.status && e.response.status !== 401) {
        console.warn('[AuthContext] refreshUser non-401 error', e);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
